// import { useLocation } from "react-router-dom";

const Utils = {
  getItemPriceRange: (item: any) => {
    if (
      !item ||
      !item.productDenominations ||
      item.productDenominations.length === 0
    ) {
      return `0€`;
    }

    const denominations = item.productDenominations.filter(
      (item: any) => item.hasOwnProperty("isMapped") && item.isMapped === true,
    );

    // Separate fixed and range denominations
    const fixedValues = denominations
      .filter((d: any) => d.type === "fixed" && d.denomination)
      .map((d: any) => d.denomination);

    const rangeValues = denominations
      .filter((d: any) => d.type === "range")
      .map((d: any) => ({
        min: d.minValue || 0,
        max: d.maxValue || 1,
      }));

    // Handle fixed denominations
    if (fixedValues.length > 0 && rangeValues.length === 0) {
      const minFixed = Math.min(...fixedValues);
      const maxFixed = Math.max(...fixedValues);
      return minFixed === maxFixed
        ? `${Utils.formatEuro(minFixed)}`
        : `${Utils.formatEuro(minFixed)} - ${Utils.formatEuro(maxFixed)}`;
    }

    // Handle range denominations
    if (rangeValues.length > 0 && fixedValues.length === 0) {
      const minRange = Math.min(...rangeValues.map((r: any) => r.min));
      const maxRange = Math.max(...rangeValues.map((r: any) => r.max));
      return `${Utils.formatEuro(minRange)} - ${Utils.formatEuro(maxRange)}`;
    }

    // Handle mix of both fixed and range
    if (fixedValues.length > 0 && rangeValues.length > 0) {
      const minFixed = Math.min(...fixedValues);
      const maxFixed = Math.max(...fixedValues);
      const minRange = Math.min(...rangeValues.map((r: any) => r.min));
      const maxRange = Math.max(...rangeValues.map((r: any) => r.max));
      const minPrice = Math.min(minFixed, minRange);
      const maxPrice = Math.max(maxFixed, maxRange);
      return `${Utils.formatEuro(minPrice)} - ${Utils.formatEuro(maxPrice)}`;
    }

    return Utils.formatEuro(0);
  },
  validatePhoneNumber: (e: any) => {
    const value = e.target.value;
    // Regular expression for phone numbers
    const regex = /^\d{10}$/;
    if (!regex.test(value)) {
      return (e.target.value = value.slice(0, -1)); // Remove the last invalid character
    }
    return value;
  },

  getRelativeDate: (date: Date): string => {
    const now = new Date();
    const diffInMs = now.getTime() - date.getTime();
    const diffInMinutes = Math.floor(diffInMs / (1000 * 60));
    const diffInHours = Math.floor(diffInMinutes / 60);
    const diffInDays = Math.floor(diffInHours / 24);

    // Recent past (minutes, hours, days, months)
    if (diffInMinutes < 1) return "Just now";
    if (diffInMinutes < 60) return `${diffInMinutes} min ago`;
    if (diffInHours < 24) return `${diffInHours} hours ago`;
    if (diffInDays < 30) return `${diffInDays} days ago`;

    // If the date is older than a month, return a formatted date
    return date.toLocaleDateString("en-US", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  },

  onlyNumber: (e: any): any => {
    return (e.target.value = e.target.value.replace(/\D/g, ""));
  },

  onlyPriceNumber(e: any) {
    const value = e.target.value;
    // Regular expression for decimals with up to 2 digits after the decimal point
    const regex = /^\d*\.?\d{0,2}$/;
    if (!regex.test(value)) {
      return (e.target.value = value.slice(0, -1)); // Remove the last invalid character
    }
  },

  formatPrice(e: any) {
    const value = e.target.value;
    // Regular expression for decimals with up to 2 digits after the decimal point
    const regex = /^\d*\.?\d{0,2}$/;
    if (!regex.test(value)) {
      return (e.target.value = value.slice(0, -1)); // Remove the last invalid character
    }
  },

  capitalizeFirstLetter: (string: string) => {
    if (!string) return ""; // Handle empty or null strings
    return string.charAt(0).toUpperCase() + string.slice(1);
  },

  extractCountryCode: (mobileNumber: any) => {
    // Remove spaces and non-numeric characters
    const cleanedNumber = mobileNumber.replace(/\D/g, "");
    // Extract everything except the last 10 digits (assumed phone number)
    const countryCode = cleanedNumber.slice(0, -10);
    return countryCode || ""; // Return germany country code if no country code found
  },

  // Reusable debounce function
  debounce: (func: Function, delay: number) => {
    let timeoutId: ReturnType<typeof setTimeout> | null = null;
    return (...args: any[]) => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      timeoutId = setTimeout(() => {
        func(...args);
      }, delay);
    };
  },

  handleUrlProtocol: (
    url: string,
    shouldAddProtocol: boolean = true,
  ): string => {
    if (!url) return "";
    const cleanedUrl = url.replace(/^(https?:\/\/)/, ""); // Remove existing http:// or https://
    // Add protocol if needed
    let finalUrl = cleanedUrl;
    if (shouldAddProtocol) {
      const protocol = "http://";
      return protocol + atob(finalUrl);
    }
    // Return base64 encoded URL if no protocol is added
    return btoa(finalUrl);
  },

  removeDuplicateAddresses: (addresses: any[]): any[] => {
    const seen = new Set();
    return addresses.filter((addr) => {
      const key = [
        addr.street.trim(),
        addr.city.trim(),
        addr.state.trim(),
        addr.postalCode.trim(),
        addr.country.trim(),
        addr.addressType.trim(),
      ]
        .join("|")
        .toLowerCase(); // optional: .toLowerCase() for case-insensitive match

      if (seen.has(key)) {
        return false;
      }
      seen.add(key);
      return true;
    });
  },

  handleProductNavigate: (
    product: any,
    navigate: any,
    countryDialingList: any[],
  ) => {
    if (!product) return;

    navigate(
      `/gift-cards/product-details/${product._id}/${product.productName}`,
    );
  },

  formatEuro: (amount: number | string): string => {
    const num = Number(amount) || 0;
    return `${num.toLocaleString("de-DE", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })} €`;
  },
};

export default Utils;
